import { create } from "zustand"

import type { CustomerPurchaseOrderSearchParams } from "../schemas"

import {
  CustomerPurchaseOrderDateRangeType,
  type CustomerPurchaseOrderListItem,
  GetCustomerPurchaseOrderListDocument,
  type GetCustomerPurchaseOrderListQuery,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

export type CustomerPurchaseOrderSearchFilters = CustomerPurchaseOrderSearchParams & {
  companyId: string
  shipToLocationId: string
}

const deduplicateCustomerPurchaseOrders = (
  newData: CustomerPurchaseOrderListItem[],
  existingData: CustomerPurchaseOrderListItem[]
): CustomerPurchaseOrderListItem[] => {
  const uniqueIds = new Set(existingData.map((cpo) => cpo.customerPurchaseOrderId))
  return [...existingData, ...newData.filter((cpo) => !uniqueIds.has(cpo.customerPurchaseOrderId))]
}

export const useCustomerPurchaseOrderSearchStore = create(
  createSearchStore<
    CustomerPurchaseOrderListItem,
    CustomerPurchaseOrderSearchFilters,
    GetCustomerPurchaseOrderListQuery
  >({
    documentNode: GetCustomerPurchaseOrderListDocument,
    deduplicateData: deduplicateCustomerPurchaseOrders,

    getQueryInput: (filters: CustomerPurchaseOrderSearchFilters, offset: number, limit: number) => {
      const { companyId, shipToLocationId, query, statuses, dateFrom, dateTo, dateRangeType } = filters
      return {
        filter: {
          companyId,
          shipToLocationId,
          query,
          statuses,
          dateFrom,
          dateTo,
          dateRangeType:
            dateRangeType ?? (dateFrom || dateTo ? CustomerPurchaseOrderDateRangeType.DeliveryDate : undefined),
        },
        offset,
        limit,
      }
    },

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: (data) => ({
      __typename: data.customerPurchaseOrder.list.__typename,
      data:
        data.customerPurchaseOrder.list.__typename === "CustomerPurchaseOrderListSuccess"
          ? (data.customerPurchaseOrder.list.customerPurchaseOrders as CustomerPurchaseOrderListItem[])
          : [],
      error:
        data.customerPurchaseOrder.list.__typename === "CustomerPurchaseOrderListFailure"
          ? data.customerPurchaseOrder.list.error
          : undefined,
    }),

    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
